/**
 * Renders a SamplePreview entry as a small static HTML table or
 * inline sparkline SVG. No real analysis is run — these are just
 * pre-computed canonical results, baked at design time, that show
 * "what Sparrow would print for this dataset". The live runs
 * happen in the real engine at sparrow.dasc.nl.
 */

import type { SamplePreview } from "@/lib/samples"

interface Props {
  preview: SamplePreview
  /** When true, render a smaller variant suited to a card or a
   *  side-by-side hero strip. */
  compact?: boolean
}

export function PreviewBlock({ preview, compact = false }: Props) {
  return (
    <figure className="rounded-lg border border-border bg-card overflow-hidden">
      <figcaption className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border bg-secondary/40">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          {previewKindLabel(preview.kind)}
        </div>
        <div className="font-medium text-sm text-foreground truncate">
          {preview.title}
        </div>
      </figcaption>
      <div className={compact ? "p-3" : "p-4"}>
        {preview.kind === "sparkline" && preview.series && (
          <Sparkline series={preview.series[0]!} />
        )}
        {preview.kind !== "sparkline" && preview.table && (
          <PreviewTable table={preview.table} compact={compact} />
        )}
        {preview.caption && (
          <p className={(compact ? "text-[11px] " : "text-xs ") + "text-muted-foreground mt-3"}>
            {preview.caption}
          </p>
        )}
      </div>
    </figure>
  )
}

function previewKindLabel(k: SamplePreview["kind"]): string {
  switch (k) {
    case "frequencies":  return "Frequencies"
    case "crosstab":     return "Crosstab"
    case "descriptives": return "Descriptives"
    case "sparkline":    return "Sparkline"
    case "anova":        return "One-way ANOVA"
  }
}

function PreviewTable({
  table,
  compact,
}: {
  table: NonNullable<SamplePreview["table"]>
  compact: boolean
}) {
  const cell = compact ? "px-2 py-1.5" : "px-3 py-2"
  return (
    <div className="overflow-x-auto">
      <table className={"w-full border-collapse " + (compact ? "text-xs" : "text-sm")}>
        <thead>
          <tr className="text-left text-muted-foreground">
            {table.headers.map((h, i) => (
              <th
                key={i}
                className={
                  cell +
                  " font-medium border-b border-border " +
                  (i > 0 ? "text-right tabular-nums" : "")
                }
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i} className="border-b border-border/60 last:border-b-0">
              {r.map((c, j) => (
                <td
                  key={j}
                  className={
                    cell +
                    " " +
                    (j > 0 ? "text-right tabular-nums" : "text-foreground")
                  }
                >
                  {c}
                </td>
              ))}
            </tr>
          ))}
          {table.total && (
            <tr className="bg-secondary/50">
              {table.total.map((c, j) => (
                <td
                  key={j}
                  className={
                    cell +
                    " font-semibold " +
                    (j > 0 ? "text-right tabular-nums" : "text-foreground")
                  }
                >
                  {c}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function Sparkline({ series }: { series: { label: string; values: number[] } }) {
  // Hand-rolled SVG sparkline. Width-flex, fixed pixel height so it
  // fits any card. Values get normalised to 0..1 vertically and to
  // 0..width horizontally on render.
  const W = 320
  const H = 80
  const PAD_Y = 6
  const max = Math.max(...series.values, 1)
  const min = Math.min(...series.values, 0)
  const range = max - min || 1
  const stepX = series.values.length > 1 ? W / (series.values.length - 1) : 0
  const points = series.values.map((v, i) => {
    const x = i * stepX
    const y = H - PAD_Y - ((v - min) / range) * (H - PAD_Y * 2)
    return [x, y] as const
  })
  const path = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(" ")
  // Filled-under variant for that "trend" look.
  const area = `${path} L ${W} ${H} L 0 ${H} Z`
  const last = points[points.length - 1]!
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-20 overflow-visible"
      role="img"
      aria-label={`${series.label} sparkline`}
    >
      <defs>
        <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="oklch(0.42 0.21 263)" stopOpacity="0.30" />
          <stop offset="100%" stopColor="oklch(0.42 0.21 263)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#spark-fill)" />
      <path
        d={path}
        fill="none"
        stroke="oklch(0.42 0.21 263)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="animate-spark-draw"
      />
      <circle cx={last[0]} cy={last[1]} r="3.5" fill="oklch(0.42 0.21 263)" />
    </svg>
  )
}
